import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { getUserPermission, UserKeys } from '../../../../../../api/users-api';
import { SharedDriveModel } from '../../../../../../../types';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import SharedDrivesRenameDialog from './shared-drives-rename-dialog';
import SharedDrivesDeleteDialog from './shared-drives-delete-dialog';
import SharedDrivesSettingsDialog from './shared-drives-settings-dialog';
import SharedDrivesPermissionDialog from './shared-drives-permission-dialog';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { handleErrors } from '../../../../../../utils/errors';
import SharedDrivesCreateDialog from './shared-drives-create-dialog';
import SharedErrorBoundary from '../../../../../../shared/shared-error-boundary';
import SharedGridStateIconButton from '../../../../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import SharedToolbar from '../../../../../../shared/shared-toolbar-latest';
import SharedDataGridClient from '../../../../../../shared/shared-data-grid-client';
import { createGridOverlays } from '../../../../../grid-overlay/no-data-overlay-helpers';
import ChangeLogRequiredOverlay from '../../../../../grid-overlay/change-log-required-overlay';

import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { useCopySharedDriveId } from '../../../../../drive/shared-drives/use-copy-shared-drive-id';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import Folder from '@mui/icons-material/Folder';
import Link from '@mui/icons-material/Link';
import Refresh from '@mui/icons-material/Refresh';
import Settings from '@mui/icons-material/Settings';
import Share from '@mui/icons-material/Share';
interface SharedDrivesGridProps {
  userEmail: string;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  sharedDrives: SharedDriveModel[] | undefined;
  isLoading: boolean;
  setOpenPermissionCheckDialog: Dispatch<SetStateAction<boolean>>;
  selectedDrive: SharedDriveModel | null;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  permissionCheckLoading: boolean;
  setPermissionCheckLoading: Dispatch<SetStateAction<boolean>>;
  showChangeLogOverlay?: boolean;
  onOpenChangeLogDialog?: () => void;
}

export default function SharedDrivesGrid({
  userEmail,
  primaryDomain,
  driveRoles,
  changeLogRoles,
  setSelectedFolder,
  sharedDrives: data,
  isLoading,
  setOpenPermissionCheckDialog,
  selectedDrive,
  setSelectedDrive,
  permissionCheckLoading,
  setPermissionCheckLoading,
  showChangeLogOverlay = false,
  onOpenChangeLogDialog,
}: SharedDrivesGridProps) {
  const queryClient = useQueryClient();
  const { viewType } = useUserSettings();

  const { setSnackbar } = useStore();
  const [openCreateSharedDriveDialog, setOpenCreateSharedDriveDialog] =
    useState(false);
  const [openSharedDrivesRenameDialog, setOpenSharedDrivesRenameDialog] =
    useState(false);
  const [openDeleteSharedDrivesDialog, setOpenDeleteSharedDrivesDialog] =
    useState(false);
  const [openSharedDrivesSettingsDialog, setOpenSharedDrivesSettingsDialog] =
    useState(false);
  const [
    openSharedDrivesPermissionsDialog,
    setOpenSharedDrivesPermissionsDialog,
  ] = useState(false);
  const { handleCopySharedDriveId, CopyChangeLogDialog } = useCopySharedDriveId(
    {
      changeLogRoles,
    }
  );

  function CustomToolbar() {
    return (
      <SharedToolbar>
        <Tooltip arrow title="Create Shared Drive">
          <IconButton
            onClick={() => {
              setOpenCreateSharedDriveDialog(true);
            }}
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanCreateSharedDrives.value
              )
            }
          >
            <Add />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Refresh">
          <IconButton
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveDocuments],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveFolders],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DrivePermissions],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveAcls],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDrivesForDomain],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDrivesForUser],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDriveFolders],
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SharedDriveDocuments],
              });
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
        <SharedGridStateIconButton
          gridStateKey="sharedDrivesGridStates"
          module={ModuleList.modules.drive.name}
          section={ModuleList.modules.directory.sections.groups}
        />
      </SharedToolbar>
    );
  }

  const columns: GridColDef<SharedDriveModel>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        display: 'flex',
        pinnable: false,
        renderCell: (params) => {
          const avatar = (
            <Tooltip title={params.row.name}>
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Avatar
                  src={params.row.backgroundImageLink}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="body2">{params.row.name}</Typography>
              </Stack>
            </Tooltip>
          );
          return avatar;
        },
        valueGetter: (_value, row) => {
          if (!row.name) return '';
          return row.name;
        },
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 150,
        type: 'date',
        display: 'flex',
        pinnable: false,
        valueGetter: (_value, row) => {
          if (row.createdDate === 0) return '';
          return new Date(row.createdDate);
        },
        valueFormatter: (value) => {
          if (value === '') return '';
          return new Date(value as string).toLocaleDateString();
        },
      },
      {
        field: 'id',
        headerName: 'Shared Drive ID',
        display: 'flex',
        flex: 0.4,
        pinnable: false,
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center">
              <Tooltip arrow title="Copy">
                <span>
                  <IconButton
                    disabled={
                      !isAbleToSeeUIElement(
                        driveRoles,
                        DriveRoles.CanCopyShareDriveID.value
                      )
                    }
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      handleCopySharedDriveId(params.row);
                    }}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={params.row.id}>
                <Typography variant="body2">{params.row.id}</Typography>
              </Tooltip>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'orgUnitId',
        headerName: 'Org Unit ID',
        display: 'flex',
        minWidth: 240,
        filterable: true,
        valueGetter: (_value, row) => {
          return row.orgUnitId;
        },
      },
      {
        field: 'hidden',
        headerName: 'Is Hidden',
        type: 'boolean',
        minWidth: 100,
        filterable: true,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params: GridRowParams) => {
          const buttonArray = [];

          buttonArray.push(
            <GridActionsCellItem
              label={'Share'}
              icon={<Share />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenSharedDrivesPermissionsDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanUpdateSharedDrivePermissions.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Settings'}
              icon={<Settings />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenSharedDrivesSettingsDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanUpdateSharedDriveSettings.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Rename'}
              icon={<DriveFileRenameOutline />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenSharedDrivesRenameDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanRenameSharedDrives.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Delete'}
              icon={<Delete />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenDeleteSharedDrivesDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanDeleteSharedDrives.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              component={'a'}
              label={'Link'}
              icon={
                <Tooltip title="Link" arrow>
                  <Link />
                </Tooltip>
              }
              // @ts-expect-error - href is valid when using component prop
              href={`https://drive.google.com/drive/folders/${params.row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          );

          return buttonArray;
        },
      },
    ],
    [driveRoles, setSelectedDrive, setSnackbar]
  );

  const uniqueRows = useMemo(
    () =>
      Array.from(new Map((data || []).map((row) => [row.id, row])).values()),
    [data]
  );

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        orgUnitId: false,
        hidden: false,
      },
    },
  };

  const overlays = showChangeLogOverlay
    ? {
        noRowsOverlay: () => (
          <ChangeLogRequiredOverlay
            onOpenDialog={() => onOpenChangeLogDialog?.()}
            permissionName="View Shared Drives"
          />
        ),
        noResultsOverlay: () => (
          <ChangeLogRequiredOverlay
            onOpenDialog={() => onOpenChangeLogDialog?.()}
            permissionName="View Shared Drives"
          />
        ),
      }
    : createGridOverlays(
        'Documents/Folders',
        <Folder />,
        'This folder is empty.'
      );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...overlays,
        }}
        defaultGridState={defaultGridState}
        keepNonExistentRowsSelected={false}
        rows={showChangeLogOverlay ? [] : uniqueRows || []}
        getRowId={(row) => row.id}
        onRowClick={async (params) => {
          try {
            setPermissionCheckLoading(true);

            const hasPermission = await queryClient.fetchQuery({
              queryKey: [
                UserKeys.SharedDriveUserPermission,
                userEmail,
                params.row.id,
              ],
              queryFn: () => getUserPermission(params.row.id, userEmail, true),
              retry: false,
              staleTime: 1000 * 60 * 2,
            });

            if (!hasPermission) {
              setSelectedDrive(params.row);
              setOpenPermissionCheckDialog(true);
            } else {
              setSelectedFolder(params.row.id);
            }
          } catch (e) {
            handleErrors({
              error: e,
              snackbarMessage: `There was an issue checking your permissions. Please try again.`,
            });
          } finally {
            setPermissionCheckLoading(false);
          }
        }}
        columns={columns}
        sx={{
          '.MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={!showChangeLogOverlay && (isLoading || permissionCheckLoading)}
        disableRowSelectionOnClick
        gridKey={viewType.sharedDrivesGridStates?.activeGridState || 'default'}
        gridStateKey="sharedDrivesGridStates"
      />
      {openCreateSharedDriveDialog ? (
        <SharedErrorBoundary section="Shared Drives Create Dialog">
          <SharedDrivesCreateDialog
            open={openCreateSharedDriveDialog}
            handleClose={() => {
              setOpenCreateSharedDriveDialog(false);
            }}
            changeLogRoles={changeLogRoles}
            driveRoles={driveRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesRenameDialog ? (
        <SharedErrorBoundary section="Shared Drives Rename Dialog">
          <SharedDrivesRenameDialog
            open={openSharedDrivesRenameDialog}
            handleClose={() => {
              setOpenSharedDrivesRenameDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openDeleteSharedDrivesDialog ? (
        <SharedErrorBoundary section="Shared Drives Delete Dialog">
          <SharedDrivesDeleteDialog
            open={openDeleteSharedDrivesDialog}
            handleClose={() => {
              setOpenDeleteSharedDrivesDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesSettingsDialog ? (
        <SharedErrorBoundary section="Shared Drives Settings Dialog">
          <SharedDrivesSettingsDialog
            open={openSharedDrivesSettingsDialog}
            handleClose={() => {
              setOpenSharedDrivesSettingsDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
            primaryDomain={primaryDomain}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesPermissionsDialog ? (
        <SharedErrorBoundary section="Shared Drives Permissions Dialog (in shared-drives-grid)">
          <SharedDrivesPermissionDialog
            open={openSharedDrivesPermissionsDialog}
            handleClose={() => {
              setOpenSharedDrivesPermissionsDialog(false);
            }}
            userEmail={userEmail}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
            driveRoles={driveRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {CopyChangeLogDialog}
    </>
  );
}
